import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  LinearProgress,
} from '@mui/material';
import { TrendingUp, OpenInNew, EmojiEvents } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '../../store/site.store';
import {
  getStrikingDistance,
  getStrikingDistanceSummary,
  StrikingDistanceItem,
} from '../../services/strikingDistanceService';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmtPos = (v: number | null | undefined) => (v != null ? v.toFixed(1) : '—');
const fmtPct = (v: number | null | undefined) => (v != null ? `${v.toFixed(0)}%` : '—');
const fmtCtr = (v: number | null | undefined) => (v != null ? `${(v * 100).toFixed(1)}%` : '—');
const fmtDateRange = (dateFrom: string | null | undefined, dateTo: string | null | undefined) => {
  if (!dateFrom || !dateTo) return null;

  const from = new Date(dateFrom);
  const to = new Date(dateTo);

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return `${dateFrom} to ${dateTo}`;
  }

  return `${from.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} to ${to.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const DifficultyChip: React.FC<{ difficulty: StrikingDistanceItem['rankingDifficulty'] }> = ({ difficulty }) => {
  const map = {
    easy: { color: 'success' as const, label: 'Easy' },
    medium: { color: 'warning' as const, label: 'Medium' },
    hard: { color: 'error' as const, label: 'Hard' },
  };
  const { color, label } = map[difficulty] ?? { color: 'default' as const, label: difficulty };
  return <Chip label={label} size="small" color={color} variant="outlined" />;
};

const ScoreBar: React.FC<{ score: number; max: number }> = ({ score, max }) => {
  const pct = max > 0 ? Math.min((score / max) * 100, 100) : 0;
  const color = pct > 66 ? '#4caf50' : pct > 33 ? '#ff9800' : '#2196f3';
  return (
    <Tooltip title={`Score: ${score?.toFixed(0)}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 100 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ flex: 1, height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: color } }}
        />
        <Typography variant="caption" sx={{ whiteSpace: 'nowrap', color }}>
          {score?.toFixed(0)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const SummaryCard: React.FC<{ label: string; value: string | number; sub?: string; color?: string }> = ({
  label,
  value,
  sub,
  color = 'primary.main',
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, color, fontWeight: 700 }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// ─── main component ──────────────────────────────────────────────────────────

export const StrikingDistanceDashboard: React.FC = () => {
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  const [difficulty, setDifficulty] = useState<string>('');
  const [viewMode, setViewMode] = useState<'latest' | 'detail'>('latest');

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['striking-distance-summary', siteId],
    queryFn: () => getStrikingDistanceSummary(siteId!),
    enabled: !!siteId,
  });

  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['striking-distance', siteId, difficulty],
    queryFn: () => getStrikingDistance(siteId!, difficulty || undefined),
    enabled: !!siteId,
  });

  const analysisRange = items.length ? fmtDateRange(items[0].dateFrom, items[0].dateTo) : null;

  const latestRows = useMemo(() => {
    const difficultyRank: Record<StrikingDistanceItem['rankingDifficulty'], number> = {
      easy: 0,
      medium: 1,
      hard: 2,
    };

    const grouped = new Map<string, {
      page: string;
      weightedPosition: number;
      weightedCtr: number;
      weightedDistance: number;
      impressions: number;
      potentialClicks: number;
      trafficUpliftPct: number;
      opportunityScore: number;
      rankingDifficulty: StrikingDistanceItem['rankingDifficulty'];
      queries: string[];
      dateFrom: string;
      dateTo: string;
    }>();

    items.forEach((item) => {
      const key = item.page;
      const impressions = item.impressions ?? 0;
      const current = grouped.get(key);

      if (!current) {
        grouped.set(key, {
          page: item.page,
          weightedPosition: (item.currentPosition ?? 0) * impressions,
          weightedCtr: (item.ctr ?? 0) * impressions,
          weightedDistance: (item.distanceToPage1 ?? 0) * impressions,
          impressions,
          potentialClicks: item.potentialClicks ?? 0,
          trafficUpliftPct: item.trafficUpliftPct ?? 0,
          opportunityScore: item.opportunityScore ?? 0,
          rankingDifficulty: item.rankingDifficulty,
          queries: item.query ? [item.query] : [],
          dateFrom: item.dateFrom,
          dateTo: item.dateTo,
        });
        return;
      }

      current.weightedPosition += (item.currentPosition ?? 0) * impressions;
      current.weightedCtr += (item.ctr ?? 0) * impressions;
      current.weightedDistance += (item.distanceToPage1 ?? 0) * impressions;
      current.impressions += impressions;
      current.potentialClicks += item.potentialClicks ?? 0;
      current.trafficUpliftPct = Math.max(current.trafficUpliftPct, item.trafficUpliftPct ?? 0);
      current.opportunityScore += item.opportunityScore ?? 0;
      if (difficultyRank[item.rankingDifficulty] < difficultyRank[current.rankingDifficulty]) {
        current.rankingDifficulty = item.rankingDifficulty;
      }
      if (item.query) current.queries.push(item.query);
      if (item.dateFrom < current.dateFrom) current.dateFrom = item.dateFrom;
      if (item.dateTo > current.dateTo) current.dateTo = item.dateTo;
    });

    return Array.from(grouped.values())
      .map((row) => {
        const divisor = row.impressions > 0 ? row.impressions : 1;
        const topQueries = Array.from(new Set(row.queries.filter(Boolean))).slice(0, 5);
        return {
          ...row,
          currentPosition: row.weightedPosition / divisor,
          ctr: row.weightedCtr / divisor,
          distanceToPage1: row.weightedDistance / divisor,
          topQueries,
        };
      })
      .sort((a, b) => (b.potentialClicks ?? 0) - (a.potentialClicks ?? 0));
  }, [items]);

  const maxScore = Math.max(...(viewMode === 'latest' ? latestRows : items).map((i: any) => i.opportunityScore ?? 0), 1);

  if (!siteId) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Select a site to view Striking Distance opportunities.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Striking Distance
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pages ranking in positions 4–20 with high impression volume. A small ranking improvement
          could unlock significant additional traffic.
        </Typography>
        {analysisRange && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Analysis window: {analysisRange}
          </Typography>
        )}
      </Box>

      {/* Summary cards */}
      {!summaryLoading && summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              label="Total Opportunities"
              value={summary.totalOpportunities.toLocaleString()}
              color="primary.main"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              label="Potential Clicks"
              value={(summary.totalPotentialClicks ?? 0).toLocaleString()}
              sub="if all reach page 1"
              color="success.main"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              label="Avg Opportunity Score"
              value={(summary.avgOpportunityScore ?? 0).toFixed(1)}
              color="warning.main"
            />
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Filter by ranking difficulty:
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={difficulty}
            exclusive
            onChange={(_, v) => setDifficulty(v ?? '')}
          >
            <ToggleButton value="">All</ToggleButton>
            <ToggleButton value="easy" sx={{ color: 'success.main' }}>
              Easy
            </ToggleButton>
            <ToggleButton value="medium" sx={{ color: 'warning.main' }}>
              Medium
            </ToggleButton>
            <ToggleButton value="hard" sx={{ color: 'error.main' }}>
              Hard
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, v) => {
            if (v) setViewMode(v);
          }}
        >
          <ToggleButton value="latest">Latest View</ToggleButton>
          <ToggleButton value="detail">Detail View</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Loading */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error">
          Failed to load striking distance data: {(error as Error).message}
        </Alert>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <Paper variant="outlined">
          {items.length === 0 ? (
            <Box p={6} textAlign="center">
              <EmojiEvents sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                No striking distance opportunities found{difficulty ? ` for difficulty: ${difficulty}` : ''}.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Page / Query</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Position
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Impressions
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      CTR
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Gap to P1
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      +Clicks if P1
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Uplift
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                      Difficulty
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(viewMode === 'latest' ? latestRows : items).map((item: any) => (
                    <TableRow key={viewMode === 'latest' ? item.page : item.id} hover>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Tooltip title={item.page}>
                          <Box
                            component="a"
                            href={item.page}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'primary.main',
                              textDecoration: 'none',
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 200,
                              mb: 0.25,
                            }}
                          >
                            {item.page}
                            <OpenInNew sx={{ fontSize: 12, flexShrink: 0 }} />
                          </Box>
                        </Tooltip>
                        {viewMode === 'latest' ? (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Top queries ({item.topQueries?.length ?? 0}):
                            </Typography>
                            {(item.topQueries?.length ? item.topQueries : ['No query detail']).slice(0, 5).map((query: string, idx: number) => (
                              <Typography
                                key={`${item.page}-q-${idx}`}
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}
                              >
                                {idx + 1}. {query}
                              </Typography>
                            ))}
                          </>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}
                          >
                            {item.query}
                          </Typography>
                        )}
                        {fmtDateRange(item.dateFrom, item.dateTo) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            Window: {fmtDateRange(item.dateFrom, item.dateTo)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<TrendingUp fontSize="small" />}
                          label={fmtPos(item.currentPosition)}
                          size="small"
                          color={item.currentPosition <= 10 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">{item.impressions?.toLocaleString()}</TableCell>
                      <TableCell align="center">{fmtCtr(item.ctr)}</TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} color="warning.main">
                          {fmtPos(item.distanceToPage1)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          +{item.potentialClicks?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{fmtPct(item.trafficUpliftPct)}</TableCell>
                      <TableCell align="center">
                        <DifficultyChip difficulty={item.rankingDifficulty} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        <ScoreBar score={item.opportunityScore} max={maxScore} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
};
